import moment from 'moment';
import 'moment-timezone';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import ActionTooltip from 'src/components/ActionTooltip';
import BadgeCmp from 'src/components/BadgeCmp';
import TableCmp from 'src/components/TableCmp';
import { CLASSIFICATION_BADGE_CONFIG, QUOTE_CLASSIFICATION } from 'src/constants/common';
import { selectCurrentLegData, selectOrder, selectOrderLoading } from 'src/redux/orders.slice';
import { convertUtcToUserTimezone, formatLinearFootage, GetAppearance, getDateWithSuffixFormat, getFormattedDateIgnoringTime, getFormattedNumber, getLabelByValue } from 'src/utils/CommonFunctions';

// Shows a date range when endDate differs from startDate, with Today/Tomorrow substitution.
const formatScheduleDateRange = (startDate?: string | null, endDate?: string | null, alwaysShowFullDate = false): string => {
    if (!startDate) return '-';
    const startFmt = getDateWithSuffixFormat(getFormattedDateIgnoringTime(startDate, 'MMM Do, YYYY', !alwaysShowFullDate, true, true));
    if (!endDate || endDate === startDate) return startFmt;
    const endFmt = getDateWithSuffixFormat(getFormattedDateIgnoringTime(endDate, 'MMM Do, YYYY', !alwaysShowFullDate, true, true));

    return `${startFmt} - ${endFmt}`;
};

// True when the given calendar date is strictly before today in the user's configured timezone.
// Compared as a plain calendar date (time component stripped) to match how the range above is rendered.
const isCalendarDateInPast = (date?: string | null): boolean => {
    if (!date) return false;
    const dateOnly = String(date).split('T')[0];
    const timezone = GetAppearance()?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    const today = typeof (moment as any).tz === 'function' ? (moment as any).tz(timezone).startOf('day') : moment().startOf('day');

    return moment(dateOnly, 'YYYY-MM-DD').isBefore(today, 'day');
};

// A scheduled pickup *window* becomes misleading once it has fully elapsed — at that point the load
// has either been picked up or not, so a "Jul 7 - Jul 10" range tells the customer nothing useful.
// Once the window's end date is in the past AND we have a real pickup timestamp, collapse the range
// down to that actual pickup date. Without an actual timestamp we keep the range as-is (no data loss).
const resolvePickupDateDisplay = (pickupSchedule: any, alwaysShowFullDate = false): string => {
    const startDate = pickupSchedule?.pickupStartDate;
    const endDate = pickupSchedule?.pickupEndDate;
    const actualPickupDatetime = pickupSchedule?.actualPickupDatetime;

    const isDateRange = Boolean(endDate && endDate !== startDate);

    if (isDateRange && actualPickupDatetime && isCalendarDateInPast(endDate)) {
        // actualPickupDatetime is stored in UTC — resolve it against the pickup location's timezone
        // so the date shown is the day the load was physically picked up.
        const actualPickupDate = convertUtcToUserTimezone(actualPickupDatetime, 'YYYY-MM-DD', true, pickupSchedule?.pickupTimezone || undefined) as string;

        return formatScheduleDateRange(actualPickupDate, null, alwaysShowFullDate);
    }

    return formatScheduleDateRange(startDate, endDate, alwaysShowFullDate);
};

// Display values shared by the desktop table row and the mobile card.
const useOrderInfoValues = (data: any) => {
    const { t } = useTranslation();

    const [secondaryEquipmentTypes, setSecondaryEquipmentTypes] = useState<string[]>([]);

    useEffect(() => {
        if (data?.secondaryEquipmentTypeFullNames) {
            setSecondaryEquipmentTypes(data?.secondaryEquipmentTypeFullNames?.split(',').map((type: any) => type.trim()));
        } else if (data?.equipmentTypeFullNames) {
            // Some API responses use equipmentTypeFullNames for secondary equipment
            setSecondaryEquipmentTypes(
                data?.equipmentTypeFullNames
                    ?.split(',')
                    .map((type: any) => type.trim())
                    .slice(1)
            );
        } else {
            setSecondaryEquipmentTypes([]);
        }
    }, [data?.secondaryEquipmentTypeFullNames, data?.equipmentTypeFullNames]);

    // Linear footage and total weight are stored per leg — the meta table shows the order-level
    // rollup, so sum across all legs. orderLegs arrives as an array or a JSON string depending on
    // the endpoint (same dual shape the orders slice and ShareOrder handle), so normalize first.
    const { totalLinearFootage, totalOrderWeight } = useMemo(() => {
        let legs: any[] = [];

        try {
            legs = Array.isArray(data?.orderLegs) ? data.orderLegs : data?.orderLegs ? JSON.parse(data.orderLegs) : [];
        } catch (e) {
            legs = [];
        }

        const sumLegField = (field: string) => legs.reduce((sum: number, leg: any) => sum + (Number(leg?.[field]) || 0), 0);

        return {
            totalLinearFootage: sumLegField('linearFootage'),
            totalOrderWeight: sumLegField('totalWeight'),
        };
    }, [data?.orderLegs]);

    const classificationBadge = useMemo(() => {
        const config = CLASSIFICATION_BADGE_CONFIG[data?.classification];
        if (!config) return null;

        return (
            <BadgeCmp style="modern" type={config.type} size="sm" mainClassName="bg-white">
                {t(config.label)}
            </BadgeCmp>
        );
    }, [data?.classification, t]);

    // Region shown after the city. The full province/state name when the record carries one, falling
    // back to the two-letter code. This header used to read stateCode only, so the same order showed
    // "Newark, NJ" here and "Newark, New Jersey" in the Orders listing — the listing's rule
    // (NewOrderRow.tsx) is the one both follow now.
    const getRegion = (address: any): string => address?.state || address?.stateCode || '';

    // Helper to build address string
    const buildCityRegion = (address: any): string => {
        const city = address?.city;
        const region = getRegion(address);

        return `${city ? (region ? `${city}, ` : city) : ''}${region}`;
    };

    const getFromAddress = () => {
        const address = data?.shipper?.shipperAddress;

        if (!address?.city && !getRegion(address)) return '-';

        return buildCityRegion(address);
    };

    const getToAddress = () => {
        const address = data?.consignee?.consigneeAddress;

        if (!address?.city && !getRegion(address)) return '-';

        return buildCityRegion(address);
    };

    const getFromAddressTooltip = () => {
        const address = data?.shipper?.shipperAddress;
        const postal = address?.postal;

        return `${buildCityRegion(address)}${postal ? `, ${postal}` : ''}`;
    };

    const getToAddressTooltip = () => {
        const address = data?.consignee?.consigneeAddress;
        const postal = address?.postal;

        return `${buildCityRegion(address)}${postal ? `, ${postal}` : ''}`;
    };

    const weightDisplay = `${totalOrderWeight > 0 ? getFormattedNumber(totalOrderWeight, false) : 0} ${
        data?.order_dimensions?.length > 0 ? getLabelByValue(data?.order_dimensions[0]?.weightMeasurement)?.toLowerCase() : 'lbs'
    }`;
    const linearFootageDisplay = totalLinearFootage > 0 ? `${formatLinearFootage(totalLinearFootage)} Ft.` : data?.classification === QUOTE_CLASSIFICATION.FTL ? '53 Ft.' : '-';

    return {
        weightDisplay,
        linearFootageDisplay,
        secondaryEquipmentTypes,
        totalLinearFootage,
        totalOrderWeight,
        classificationBadge,
        getFromAddress,
        getToAddress,
        getFromAddressTooltip,
        getToAddressTooltip,
    };
};

const OrderDetailMetaDataTableRow = ({ data, equipmentTypeFullName, alwaysShowFullDate }: { data: any; equipmentTypeFullName?: string; alwaysShowFullDate?: boolean }) => {
    const { t } = useTranslation();

    const fromAddressRef = useRef<HTMLParagraphElement>(null);
    const toAddressRef = useRef<HTMLParagraphElement>(null);

    const { secondaryEquipmentTypes, totalLinearFootage, totalOrderWeight, classificationBadge, getFromAddress, getToAddress, getFromAddressTooltip, getToAddressTooltip } = useOrderInfoValues(data);

    return (
        <>
            {/* From */}
            <td className="md:px-4 md:w-[14.28%] md:max-w-[14.28%] md:min-w-[150px] space-y-1">
                <h6 className="table-metadata-heading">{t('Common.From')}</h6>
                <div className="truncate w-fit flex">
                    <ActionTooltip message={getFromAddressTooltip()} isOpenChildTopPosOnTop openOnHover tooltipPosition="center" parentClassName="max-w-[400px] w-max" type="light">
                        <span ref={fromAddressRef} className="truncate text-[14px] max-w-[180px] block leading-[20px] font-medium w-fit text-textSecondary">
                            {getFromAddress()}
                        </span>
                    </ActionTooltip>
                </div>
            </td>

            {/* To */}
            <td className="md:px-5 md:w-[14.28%] md:max-w-[14.28%] md:min-w-[150px] space-y-1">
                <h6 className="table-metadata-heading">{t('Common.To')}</h6>
                <div className="truncate w-fit flex">
                    <ActionTooltip
                        message={getToAddressTooltip()}
                        isOpenChildTopPosOnTop
                        openOnHover
                        tooltipPosition="center"
                        parentClassName="max-w-[400px] w-max"
                        childrenClassName="flex"
                        type="light"
                    >
                        <p ref={toAddressRef} className="truncate text-[14px] max-w-[180px] block leading-[20px] font-medium text-textSecondary">
                            {getToAddress()}
                        </p>
                    </ActionTooltip>
                </div>
            </td>

            {/* Pickup Date */}
            <td className="md:px-5 md:w-[14.28%] md:max-w-[14.28%] md:min-w-[150px] space-y-1">
                <h6 className="table-metadata-heading">{t('Common.Pickup_Date')}</h6>
                {classificationBadge ? (
                    <div>{classificationBadge}</div>
                ) : (
                    <p
                        className="text-[14px] leading-[20px] font-medium text-textSecondary truncate"
                        dangerouslySetInnerHTML={{
                            __html: resolvePickupDateDisplay(data?.orderPickupSchedule, alwaysShowFullDate),
                        }}
                    />
                )}
            </td>

            {/* Est. Delivery Date */}
            <td className="md:px-5 md:w-[14.28%] md:max-w-[14.28%] md:min-w-[150px] space-y-1">
                <h6 className="table-metadata-heading">{t('QuotingHub.Common.Est_Delivery_Date')}</h6>
                <p
                    className="text-textSecondary text-[14px] font-medium leading-[20px] truncate"
                    dangerouslySetInnerHTML={{
                        __html: formatScheduleDateRange(data?.orderDeliverySchedule?.deliveryStartDate, data?.orderDeliverySchedule?.deliveryEndDate, alwaysShowFullDate),
                    }}
                />
            </td>

            {/* Total Weight */}
            <td className="md:px-5 md:w-[14.28%] md:max-w-[14.28%] md:min-w-[150px] space-y-1">
                <h6 className="table-metadata-heading">{t('Common.Total_Weight')}</h6>
                <p className="text-textSecondary text-[14px] font-medium leading-[20px] truncate">
                    {totalOrderWeight > 0 ? getFormattedNumber(totalOrderWeight, false) : 0}{' '}
                    {data?.order_dimensions?.length > 0 ? getLabelByValue(data?.order_dimensions[0]?.weightMeasurement)?.toLowerCase() : 'lbs'}
                </p>
            </td>

            {/* Linear Footage */}
            <td className="md:px-5 md:w-[14.28%] md:max-w-[14.28%] md:min-w-[150px] space-y-1">
                <h6 className="table-metadata-heading">{t('Common.Linear_Footage')}</h6>
                <p className="text-textSecondary text-[14px] font-medium leading-[20px] truncate">
                    {totalLinearFootage > 0 ? `${formatLinearFootage(totalLinearFootage)} Ft.` : data?.classification === QUOTE_CLASSIFICATION.FTL ? '53 Ft.' : '-'}
                </p>
            </td>

            {/* Equipment Type */}
            <td className="md:px-5 md:w-[14.28%] md:max-w-[14.28%] md:min-w-[150px] space-y-1">
                <h6 className="table-metadata-heading">{t('Common.Equipment_Type')}</h6>
                <div className="flex items-center gap-1">
                    <div className="truncate flex items-center gap-1">
                        {equipmentTypeFullName || data?.equipmentTypeFullName || data?.primaryEquipmentTypeFullName ? (
                            <BadgeCmp style="modern" type="pink" size="sm" mainClassName="bg-white">
                                {equipmentTypeFullName || data?.equipmentTypeFullName || data?.primaryEquipmentTypeFullName}
                            </BadgeCmp>
                        ) : (
                            <p className="text-xs leading-[20px] font-medium text-textSecondary">-</p>
                        )}

                        {secondaryEquipmentTypes.length > 0 && (
                            <ActionTooltip
                                openOnHover
                                parentClassName="min-w-fit max-w-max w-52"
                                tooltipPosition={secondaryEquipmentTypes.length <= 2 ? 'center' : 'right'}
                                isOpenChildTopPosOnTop
                                childrenClassName="flex"
                                message={secondaryEquipmentTypes.slice(0).join(', ')}
                            >
                                <BadgeCmp style="modern" type="gray" size="sm" mainClassName="bg-white" isHidePillDot>
                                    +{secondaryEquipmentTypes.length}
                                </BadgeCmp>
                            </ActionTooltip>
                        )}
                    </div>
                </div>
            </td>
        </>
    );
};

const OrderDetailMetaDataTableLoadingRow = () => {
    const { t } = useTranslation();

    return (
        <>
            {/* From */}
            <td className="md:px-4 md:w-[14.28%] md:max-w-[14.28%] md:min-w-[150px] space-y-1">
                <h6 className="table-metadata-heading">{t('Common.From')}</h6>
                <p className="text-textSecondary text-xs font-medium leading-[20px] truncate custom-loading">Lorem Ipsum</p>
            </td>

            {/* To */}
            <td className="md:px-4 md:w-[14.28%] md:max-w-[14.28%] md:min-w-[150px] space-y-1">
                <h6 className="table-metadata-heading">{t('Common.To')}</h6>
                <p className="text-textSecondary text-xs font-medium leading-[20px] truncate custom-loading">Lorem Ipsum</p>
            </td>

            {/* Pickup Date */}
            <td className="md:px-4 md:w-[14.28%] md:max-w-[14.28%] md:min-w-[150px] space-y-1">
                <h6 className="table-metadata-heading">{t('Common.Pickup_Date')}</h6>
                <p className="text-textSecondary text-[14px] font-medium leading-[20px] truncate custom-loading">Lorem Ipsum</p>
            </td>

            {/* Est. Delivery Date */}
            <td className="md:px-4 md:w-[14.28%] md:max-w-[14.28%] md:min-w-[150px] space-y-1">
                <h6 className="table-metadata-heading">{t('QuotingHub.Common.Est_Delivery_Date')}</h6>
                <p className="text-textSecondary text-xs font-medium leading-[20px] truncate custom-loading">Lorem Ipsum</p>
            </td>

            {/* Total Weight */}
            <td className="md:px-4 md:w-[14.28%] md:max-w-[14.28%] md:min-w-[150px] space-y-1">
                <h6 className="table-metadata-heading">{t('Common.Total_Weight')}</h6>
                <p className="text-textSecondary text-xs font-medium leading-[20px] truncate custom-loading">Lorem Ipsum</p>
            </td>

            {/* Linear Footage */}
            <td className="md:px-4 md:w-[14.28%] md:max-w-[14.28%] md:min-w-[150px] space-y-1">
                <h6 className="table-metadata-heading">{t('Common.Linear_Footage')}</h6>
                <p className="text-textSecondary text-xs font-medium leading-[20px] truncate custom-loading">Lorem Ipsum</p>
            </td>

            {/* Equipment Type */}
            <td className="md:px-4 md:w-[14.28%] md:max-w-[14.28%] md:min-w-[150px] space-y-1">
                <h6 className="table-metadata-heading">{t('Common.Equipment_Type')}</h6>
                <p className="text-textSecondary text-xs font-medium leading-[20px] truncate custom-loading">Lorem Ipsum</p>
            </td>
        </>
    );
};

// Mobile (< md) layout: From → To route timeline, then one card of label / value rows (Scheduling
// Details style). Same values/tooltips as the desktop row (useOrderInfoValues).
const MOBILE_CARD = 'bg-white border border-utilityGray200 rounded-lg shadow-xs';
const MOBILE_ROW_LABEL = 'text-xs leading-[18px] font-normal text-grayLight600 flex-none';
const MOBILE_ROW_VALUE = 'text-xs leading-[18px] font-medium text-grayLight600 text-right truncate min-w-0';
const MOBILE_ROUTE_LABEL = 'text-[10px] leading-[14px] font-medium tracking-[0.4px] uppercase text-gray500';
const MOBILE_ROUTE_VALUE = 'text-[13px] leading-[18px] font-medium text-grayLight900 truncate';

const OrderInfoMobileCard = ({ data, equipmentTypeFullName, alwaysShowFullDate, isLoading }: { data: any; equipmentTypeFullName?: string; alwaysShowFullDate?: boolean; isLoading: boolean }) => {
    const { t } = useTranslation();
    const { weightDisplay, linearFootageDisplay, secondaryEquipmentTypes, classificationBadge, getFromAddress, getToAddress, getFromAddressTooltip, getToAddressTooltip } = useOrderInfoValues(data);
    const equipmentName = equipmentTypeFullName || data?.equipmentTypeFullName || data?.primaryEquipmentTypeFullName;
    const loadingClass = isLoading ? 'custom-loading w-fit' : '';

    const renderRow = (label: string, value: React.ReactNode) => (
        <div className="flex items-center justify-between gap-x-4 min-w-0">
            <span className={MOBILE_ROW_LABEL}>{label}</span>
            {value}
        </div>
    );

    return (
        <div className="flex flex-col gap-2">
            {/* Route timeline */}
            <div className={`${MOBILE_CARD} p-3.5 flex flex-col`}>
                <div className="flex gap-3">
                    <div className="flex flex-col items-center gap-1 pt-[3px] w-3 flex-none self-stretch">
                        <span className="w-2.5 h-2.5 rounded-full border-2 border-primary bg-white flex-none" />
                        <span className="w-0.5 flex-1 bg-utilityGray200" />
                    </div>
                    <div className="flex-1 min-w-0 flex flex-col gap-0.5 pb-4">
                        <p className={MOBILE_ROUTE_LABEL}>{t('Common.From')}</p>
                        {isLoading ? (
                            <p className={`${MOBILE_ROUTE_VALUE} ${loadingClass}`}>Lorem Ipsum</p>
                        ) : (
                            <ActionTooltip
                                message={getFromAddressTooltip()}
                                isOpenChildTopPosOnTop
                                openOnHover
                                tooltipPosition="center"
                                parentClassName="max-w-[400px] w-max"
                                childrenClassName="flex min-w-0"
                                type="light"
                            >
                                <p className={MOBILE_ROUTE_VALUE}>{getFromAddress()}</p>
                            </ActionTooltip>
                        )}
                    </div>
                </div>
                <div className="flex gap-3">
                    <div className="flex flex-col items-center pt-[3px] w-3 flex-none">
                        <span className="w-2.5 h-2.5 rounded-full bg-successSecondary flex-none" />
                    </div>
                    <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                        <p className={MOBILE_ROUTE_LABEL}>{t('Common.To')}</p>
                        {isLoading ? (
                            <p className={`${MOBILE_ROUTE_VALUE} ${loadingClass}`}>Lorem Ipsum</p>
                        ) : (
                            <ActionTooltip
                                message={getToAddressTooltip()}
                                isOpenChildTopPosOnTop
                                openOnHover
                                tooltipPosition="center"
                                parentClassName="max-w-[400px] w-max"
                                childrenClassName="flex min-w-0"
                                type="light"
                            >
                                <p className={MOBILE_ROUTE_VALUE}>{getToAddress()}</p>
                            </ActionTooltip>
                        )}
                    </div>
                </div>
            </div>

            {/* Details — one card of label / value rows (Scheduling Details style) */}
            <div className={`${MOBILE_CARD} p-2 flex flex-col gap-2`}>
                {renderRow(
                    t('Common.Pickup_Date'),
                    !isLoading && classificationBadge ? (
                        <div className="flex-none">{classificationBadge}</div>
                    ) : (
                        <p
                            className={`${MOBILE_ROW_VALUE} ${loadingClass}`}
                            dangerouslySetInnerHTML={{ __html: isLoading ? 'Lorem Ipsum' : resolvePickupDateDisplay(data?.orderPickupSchedule, alwaysShowFullDate) }}
                        />
                    )
                )}
                {renderRow(
                    t('QuotingHub.Common.Est_Delivery_Date'),
                    <p
                        className={`${MOBILE_ROW_VALUE} ${loadingClass}`}
                        dangerouslySetInnerHTML={{
                            __html: isLoading
                                ? 'Lorem Ipsum'
                                : formatScheduleDateRange(data?.orderDeliverySchedule?.deliveryStartDate, data?.orderDeliverySchedule?.deliveryEndDate, alwaysShowFullDate),
                        }}
                    />
                )}
                {renderRow(t('Common.Total_Weight'), <p className={`${MOBILE_ROW_VALUE} ${loadingClass}`}>{isLoading ? 'Lorem Ipsum' : weightDisplay}</p>)}
                {renderRow(t('Common.Linear_Footage'), <p className={`${MOBILE_ROW_VALUE} ${loadingClass}`}>{isLoading ? 'Lorem Ipsum' : linearFootageDisplay}</p>)}
                {renderRow(
                    t('Common.Equipment_Type'),
                    <div className="flex items-center gap-1 min-w-0">
                        {isLoading ? (
                            <p className={`${MOBILE_ROW_VALUE} custom-loading`}>Lorem Ipsum</p>
                        ) : equipmentName ? (
                            <BadgeCmp style="modern" type="pink" mainClassName="bg-white">
                                {equipmentName}
                            </BadgeCmp>
                        ) : (
                            <p className={MOBILE_ROW_VALUE}>-</p>
                        )}
                        {!isLoading && secondaryEquipmentTypes.length > 0 && (
                            <ActionTooltip
                                openOnHover
                                parentClassName="min-w-fit max-w-max w-52"
                                tooltipPosition="right"
                                isOpenChildTopPosOnTop
                                childrenClassName="flex"
                                message={secondaryEquipmentTypes.join(', ')}
                            >
                                <BadgeCmp style="modern" type="gray" mainClassName="bg-white" isHidePillDot>
                                    +{secondaryEquipmentTypes.length}
                                </BadgeCmp>
                            </ActionTooltip>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const OrderInfoSection = ({ alwaysShowFullDate = false }: { alwaysShowFullDate?: boolean }) => {
    // Redux selectors
    const order = useSelector(selectOrder);
    const isLoading = useSelector(selectOrderLoading);
    const currentLegData = useSelector(selectCurrentLegData);

    // Equipment type lives on the leg, not the root order object
    const equipmentTypeFullName = currentLegData?.equipmentTypeFullName || order?.orderLegs?.[0]?.equipmentTypeFullName || order?.equipmentTypeFullName || order?.primaryEquipmentTypeFullName;

    return (
        <>
            <div className="md:hidden">
                <OrderInfoMobileCard data={order} equipmentTypeFullName={equipmentTypeFullName} alwaysShowFullDate={alwaysShowFullDate} isLoading={isLoading || !order} />
            </div>
            <div className="max-md:hidden w-full rounded-xl shadow-sm border border-utilityGray200 flex justify-between flex-col bg-white">
                <div className="md:overflow-x-auto custom-scrollbar scrollbar-hide table-without-pagination">
                    <TableCmp
                        tableHeaderClass="!hidden"
                        tableDataArr={order ? [order] : []}
                        TableLoaderRowCmp={OrderDetailMetaDataTableLoadingRow}
                        TableRowCmp={OrderDetailMetaDataTableRow}
                        isTableDataLoading={isLoading || !order}
                        tableBodyRowClass="max-md:!grid max-md:!grid-cols-2 max-md:gap-3 max-md:[&>td]:!h-auto max-md:[&>td]:!p-0 md:[&>td:not(:first-child):not(:last-child)]:!px-4 md:[&>td:first-child]:!pr-4 md:[&>td:first-child]:!pl-4 md:[&>td:first-child]:!py-2.5 md:[&>td:last-child]:!pl-4 md:[&>td:last-child]:!py-2.5 overflow-hidden md:[&>td:first-child]:!rounded-l-lg md:[&>td:last-child]:!rounded-r-lg"
                        numberOfSkeletonRows={1}
                        tableRowCmpProps={{ equipmentTypeFullName, alwaysShowFullDate }}
                    />
                </div>
            </div>
        </>
    );
};

export default OrderInfoSection;
